import React, { useState, useEffect } from 'react';
import { X, ChevronRight, ChevronLeft, Target, CheckSquare, BookOpen, Zap, Calendar } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';

const TOUR_STORAGE_KEY = 'strategic_architect_tour_completed';

const tourSteps = [
  {
    id: 'welcome',
    title: 'Welcome to Strategic Architect!',
    description: 'Let\'s take a quick tour to help you get started with strategic planning.',
    icon: Target,
    action: null,
  },
  {
    id: 'goals',
    title: 'Create Strategic Goals',
    description: 'Start by creating your first strategic goal. Add action items with due dates to track your progress.',
    icon: Target,
    action: '/goals',
    tip: 'Tip: Use the "Add Goal" button to create your first goal with 2-3 action items.',
  },
  {
    id: 'tasks',
    title: 'Manage Your Tasks',
    description: 'Break down your work into actionable tasks. Set priorities and due dates to stay organized.',
    icon: CheckSquare,
    action: '/tasks',
    tip: 'Tip: Link tasks to goals to see how daily work contributes to strategic objectives.',
  },
  {
    id: 'learning',
    title: 'Learn Strategic Frameworks',
    description: 'Explore 27 learning modules covering strategic management, lean operations, and more.',
    icon: BookOpen,
    action: '/modules',
    tip: 'Tip: Start with "Strategic Thinking Fundamentals" for a solid foundation.',
  },
  {
    id: 'coach',
    title: 'Get AI-Powered Guidance',
    description: 'The AI Coach provides personalized strategic advice based on your goals and progress.',
    icon: Zap,
    action: '/coach',
    tip: 'Tip: Ask the AI Coach for help prioritizing your goals or overcoming obstacles.',
  },
  {
    id: 'prompts',
    title: 'Daily Reflection',
    description: 'Answer daily prompts to build strategic thinking habits and track your journey.',
    icon: Calendar,
    action: '/prompts',
    tip: 'Tip: Spend 5 minutes each day reflecting on your strategic priorities.',
  },
  {
    id: 'complete',
    title: 'You\'re Ready!',
    description: 'You now know the basics. Start by creating your first strategic goal!',
    icon: Target,
    action: '/goals',
  },
];

const OnboardingTour = ({ forceShow = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if tour has been completed
    if (forceShow) {
      setIsOpen(true);
      return;
    }
    
    const tourCompleted = localStorage.getItem(TOUR_STORAGE_KEY);
    if (!tourCompleted) {
      // Auto-show tour for new users after a short delay
      const timer = setTimeout(() => setIsOpen(true), 2000);
      return () => clearTimeout(timer);
    }
  }, [forceShow]);

  const handleNext = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    localStorage.setItem(TOUR_STORAGE_KEY, 'true');
  };

  const handleGoToStep = () => {
    const step = tourSteps[currentStep];
    if (step.action) {
      navigate(step.action);
    }
    if (currentStep === tourSteps.length - 1) {
      handleClose();
    }
  };

  const handleSkip = () => {
    handleClose();
  };

  if (!isOpen) return null;

  const step = tourSteps[currentStep];
  const StepIcon = step.icon;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4" data-testid="onboarding-tour">
      <div className="bg-white dark:bg-[#111827] rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
        {/* Progress bar */}
        <div className="h-1 bg-slate-200 dark:bg-slate-700">
          <div 
            className="h-full bg-gradient-to-r from-[#26C6DA] to-[#8B5CF6] transition-all duration-300"
            style={{ width: `${((currentStep + 1) / tourSteps.length) * 100}%` }}
          />
        </div>

        {/* Header */}
        <div className="p-6 pb-0 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-gradient-to-br from-[#26C6DA]/20 to-[#8B5CF6]/20">
              <StepIcon className="w-6 h-6 text-[#26C6DA]" />
            </div>
            <div>
              <span className="text-xs text-slate-500 dark:text-slate-400">
                Step {currentStep + 1} of {tourSteps.length}
              </span>
              <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Outfit' }}>
                {step.title}
              </h2>
            </div>
          </div>
          <button 
            onClick={handleClose}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-slate-600 dark:text-slate-300 mb-4">
            {step.description}
          </p>
          
          {step.tip && (
            <div className="p-4 bg-[#26C6DA]/10 rounded-xl border border-[#26C6DA]/30 mb-4">
              <p className="text-sm text-[#26C6DA]">{step.tip}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 pt-0 flex items-center justify-between">
          <button 
            onClick={handleSkip}
            className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
          >
            Skip Tour
          </button>
          
          <div className="flex items-center gap-2">
            {currentStep > 0 && (
              <Button
                onClick={handlePrev}
                variant="outline"
                className="rounded-full border-slate-300 dark:border-slate-600"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            )}
            
            {currentStep < tourSteps.length - 1 ? (
              <Button
                onClick={handleNext}
                className="rounded-full bg-gradient-to-r from-[#26C6DA] to-[#8B5CF6] hover:from-[#0891B2] hover:to-[#7C3AED] text-white"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button
                onClick={handleGoToStep}
                className="rounded-full bg-gradient-to-r from-[#26C6DA] to-[#8B5CF6] hover:from-[#0891B2] hover:to-[#7C3AED] text-white"
              >
                Get Started
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Export a hook to trigger the tour from settings
export const useTriggerTour = () => {
  const resetTour = () => {
    localStorage.removeItem(TOUR_STORAGE_KEY);
    window.location.reload();
  };
  return { resetTour };
};

export default OnboardingTour;
